import React, { useState, useEffect } from "react";
// react component for creating dynamic tables
import ReactTable from "react-table";
import SweetAlert from "react-bootstrap-sweetalert";

import { useSelector, useDispatch } from "react-redux";
// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
// @material-ui/icons
import EditIcon from "@material-ui/icons/Edit";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import SupervisedUserCircleIcon from "@material-ui/icons/SupervisedUserCircle";
import BlockIcon from "@material-ui/icons/Block";
// core components
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import Card from "components/Card/Card.js";
import CustomInput from "components/CustomInput/CustomInput";
import CardBody from "components/Card/CardBody.js";
import CardIcon from "components/Card/CardIcon.js";
import CardHeader from "components/Card/CardHeader.js";
import { cardTitle } from "assets/jss/material-dashboard-pro-react.js";
import NewAccountDialog from "./NewAgentDialog";
import { accountsActions } from "redux/accounts/actions";
import alertStyles from "assets/jss/material-dashboard-pro-react/views/sweetAlertStyle.js";
import { validateEmail } from "redux/utils";

const styles = {
  cardIconTitle: {
    ...cardTitle,
    marginTop: "15px",
    marginBottom: "0px",
  },
  input: {
    paddingTop: 0,
  },
};

const useStyles = makeStyles({
  ...styles,
  ...alertStyles,
});

function filterCaseInsensitive(filter, row) {
  const id = filter.pivotId || filter.id;
  return row[id] !== undefined
    ? String(row[id].toLowerCase()).startsWith(filter.value.toLowerCase())
    : true;
}

const AccountsManagement = ({ match }) => {
  const dispatch = useDispatch();
  const [isNewTruckVisible, setNewTruckVisible] = useState(false);
  const [alert, setAlert] = useState(null);
  const [isEdit, setEdit] = useState(false);
  const [account, setAccount] = useState({});
  const {
    accounts,
    saving,
    savingSuccess,
    deleteSuccess,
    activateSuccess,
    resetSuccess,
  } = useSelector((state) => state.accounts);
  const [data, setData] = useState([]);

  useEffect(() => {
    dispatch(accountsActions.fetchAccounts(match.params.branch_id));
  }, []);
  useEffect(() => {
    if (savingSuccess) {
      setNewTruckVisible(false);
      const action = isEdit ? "updated" : "added";
      setAlert(
        <SweetAlert
          success
          style={{ display: "block", marginTop: "-100px" }}
          title={`Account ${action}`}
          onConfirm={onDismissSave}
          onCancel={onDismissSave}
          confirmBtnCssClass={classes.button + " " + classes.success}
        >
          The Account has been {isEdit ? "updated" : "added"}.
        </SweetAlert>
      );
    }
  }, [savingSuccess]);

  useEffect(() => {
    if (activateSuccess) {
      setAlert(
        <SweetAlert
          success
          style={{ display: "block", marginTop: "-100px" }}
          title="Activated!"
          onConfirm={onCancelActive}
          onCancel={onCancelActive}
          confirmBtnCssClass={classes.button + " " + classes.success}
        >
          The Account is active and will have the access to the portal again.
        </SweetAlert>
      );
    }
  }, [activateSuccess]);

  useEffect(() => {
    if (deleteSuccess) {
      setAlert(
        <SweetAlert
          success
          style={{ display: "block", marginTop: "-100px" }}
          title="Blocked!"
          onConfirm={onCancel}
          onCancel={onCancel}
          confirmBtnCssClass={classes.button + " " + classes.success}
        >
          The Account has been blocked.
        </SweetAlert>
      );
    }
  }, [deleteSuccess]);

  useEffect(() => {
    if (resetSuccess) {
      setAlert(
        <SweetAlert
          success
          style={{ display: "block", marginTop: "-100px" }}
          title="Account reset!"
          onConfirm={onDismissReset}
          onCancel={onDismissReset}
          confirmBtnCssClass={classes.button + " " + classes.success}
        >
          The Account has been reset.
        </SweetAlert>
      );
    }
  }, [resetSuccess]);

  const onCancel = () => {
    setAlert(null);
    dispatch(accountsActions.resetSuccessDelete());
  };
  const onCancelActive = () => {
    setAlert(null);
    dispatch(accountsActions.resetActivateDelete());
  };

  const onDismissSave = () => {
    setAlert(null);
    dispatch(accountsActions.resetSuccessSave());
  };
  const onDismissReset = () => {
    setAlert(null);
    dispatch(accountsActions.resetSuccessReset());
  };

  const warningWithResetMessage = (obj) => {
    setAlert(
      <SweetAlert
        warning
        style={{ display: "block", marginTop: "-100px" }}
        title="Are you sure?"
        onConfirm={() => onReset(obj)}
        onCancel={() => setAlert(null)}
        confirmBtnCssClass={classes.button + " " + classes.success}
        cancelBtnCssClass={classes.button + " " + classes.danger}
        confirmBtnText="Yes, Reset it!"
        cancelBtnText="Cancel"
        showCancel
      >
        The Account password will be reset and notified by the account email.
      </SweetAlert>
    );
  };
  const warningWithConfirmMessage = (obj) => {
    setAlert(
      <SweetAlert
        warning
        style={{ display: "block", marginTop: "-100px" }}
        title="Are you sure?"
        onConfirm={() => onDelete(obj)}
        onCancel={() => setAlert(null)}
        confirmBtnCssClass={classes.button + " " + classes.success}
        cancelBtnCssClass={classes.button + " " + classes.danger}
        confirmBtnText="Yes, Block it!"
        cancelBtnText="Cancel"
        showCancel
      >
        The Account will not have access to the portal.
      </SweetAlert>
    );
  };
  const activateWithConfirmMessage = (obj) => {
    setAlert(
      <SweetAlert
        warning
        style={{ display: "block", marginTop: "-100px" }}
        title="Are you sure?"
        onConfirm={() => onActivate(obj)}
        onCancel={() => setAlert(null)}
        confirmBtnCssClass={classes.button + " " + classes.success}
        cancelBtnCssClass={classes.button + " " + classes.danger}
        confirmBtnText="Yes, Activate it!"
        cancelBtnText="Cancel"
        showCancel
      >
        The Account will have access again to his account.
      </SweetAlert>
    );
  };

  const onDelete = (obj) => {
    dispatch(accountsActions.deleteAccount(obj.id));
  };
  const onReset = (obj) => {
    dispatch(accountsActions.resetAccount(obj.id));
  };
  const onActivate = (obj) => {
    dispatch(accountsActions.activateAccount(obj.id));
  };

  const onSubmit = () => {
    let firstNameState = account.firstNameState;
    let lastNameState = account.lastNameState;
    let phoneNumberState = account.phoneNumberState;
    let emailState = account.emailState;

    if (!account.first_name) {
      firstNameState = "error";
    }
    if (!account.last_name) {
      lastNameState = "error";
    }
    if (!account.email) {
      emailState = "error";
    }
    if (
      firstNameState === "error" ||
      lastNameState === "error" ||
      phoneNumberState === "error" ||
      emailState === "error"
    ) {
      setAccount({
        ...account,
        firstNameState,
        lastNameState,
        phoneNumberState,
        emailState,
      });
    } else {
      account.branch_id = match.params.branch_id;
      account.client_id = match.params.client_id;
      account.status = 1;
      dispatch(accountsActions.saveAccount(account));
    }
  };

  const onDataChanged = (key, value) => {
    let firstNameState = account.firstNameState;
    let lastNameState = account.lastNameState;
    let phoneNumberState = account.phoneNumberState;
    let emailState = account.emailState;
    if (key === "first_name") {
      if (!value) firstNameState = "error";
      else firstNameState = "success";
    }
    if (key === "last_name") {
      if (!value) lastNameState = "error";
      else lastNameState = "success";
    }
    if (key === "email") {
      if (!value || !validateEmail(value)) emailState = "error";
      else emailState = "success";
    }
    if (key === "phone_number") {
      if (value) {
        if (value.length < 9) phoneNumberState = "error";
        else phoneNumberState = "success";
      } else {
        phoneNumberState = "";
      }
    }

    setAccount({
      ...account,
      [key]: value,
      firstNameState,
      lastNameState,
      phoneNumberState,
      emailState,
    });
  };

  const onOpenDialog = () => {
    setAccount({
      first_name: "",
      last_name: "",
      phone_number: "",
      email: "",
      firstNameState: "",
      lastNameState: "",
      phoneNumberState: "",
      emailState: "",
    });
    setEdit(false);
    setNewTruckVisible(true);
  };

  const onEdit = (account) => {
    setEdit(true);
    setAccount(account);
    setNewTruckVisible(true);
  };

  const getTrProps = (state, rowInfo) => {
    if (rowInfo) {
      return {
        style: {
          background: rowInfo.row._original.status == 0 && "#FFCCCB",
          //color: 'white'
        },
      };
    }
    return {};
  };

  useEffect(() => {
    setData(
      accounts.map((prop) => {
        return {
          id: prop.id,
          first_name: prop.first_name,
          last_name: prop.last_name,
          phone_number: prop.phone_number || "N/A",
          email: prop.email,
          user_id: prop.user_id,
          status: prop.status,
          actions: (
            // we've added some custom button actions
            <div className="actions-right">
              {prop.status == 1 && (
                <Button
                  justIcon
                  round
                  simple
                  onClick={() => {
                    let account = accounts.find((o) => o.id === prop.id);
                    onEdit(account);
                  }}
                  size="lg"
                  color="primary"
                  className="edit"
                >
                  <EditIcon />
                </Button>
              )}
              {/* use this button to remove the data row */}
              <Button
                onClick={() => {
                  const obj = accounts.find((o) => o.id === prop.id);
                  warningWithResetMessage(obj);
                }}
                size="sm"
                color="warning"
                className="remove"
              >
                Reset
              </Button>
              {prop.status == 1 ? (
                <Button
                  onClick={() => {
                    const obj = accounts.find((o) => o.id === prop.id);
                    warningWithConfirmMessage(obj);
                  }}
                  size="sm"
                  color="danger"
                  className="remove"
                >
                  Block
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    const obj = accounts.find((o) => o.id === prop.id);
                    activateWithConfirmMessage(obj);
                  }}
                  size="sm"
                  color="success"
                >
                  Activate
                </Button>
              )}
            </div>
          ),
        };
      })
    );
  }, [accounts]);

  const classes = useStyles();
  return (
    <GridContainer>
      <GridItem xs={12}>
        <Button
          onClick={onOpenDialog}
          style={{ float: "right", marginBottom: 10 }}
          size="lg"
          color="primary"
          variant="contained"
        >
          Add New Account
        </Button>
        <Card>
          <CardHeader color="primary" icon>
            <CardIcon color="primary">
              <SupervisedUserCircleIcon />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>
              Accounts Management ({accounts.length})
            </h4>
          </CardHeader>
          <CardBody>
            <ReactTable
              FilterComponent={({ filter, onChange }) => (
                <CustomInput
                  value={filter ? filter.value : ""}
                  onChange={(value) => onChange(value)}
                  id="success"
                  // labelText="Plate number"
                  formControlProps={{
                    fullWidth: true,
                    className: classes.input,
                  }}
                  inputProps={{
                    value: filter ? filter.value : "",
                  }}
                />
              )}
              data={data}
              filterable
              getTrProps={getTrProps}
              defaultFilterMethod={filterCaseInsensitive}
              columns={[
                {
                  Header: "First Name",
                  accessor: "first_name",
                },
                {
                  Header: "Last Name",
                  accessor: "last_name",
                },
                {
                  Header: "Phone Number",
                  accessor: "phone_number",
                },
                {
                  Header: "Email",
                  accessor: "email",
                },
                // {
                //   Header: "Verified",
                //   Cell: props => (
                //     <label
                //       style={{
                //         color: props.original.user_id ? "green" : "red"
                //       }}
                //     >
                //       {props.original.verified === 0 ? "NO" : "YES"}
                //     </label>
                //   )
                // },
                {
                  Header: "Actions",
                  accessor: "actions",
                  sortable: false,
                  filterable: false,
                },
              ]}
              defaultPageSize={10}
              showPaginationTop
              showPaginationBottom={false}
              className="-striped -highlight"
            />
          </CardBody>
        </Card>
      </GridItem>
      <NewAccountDialog
        onClose={() => setNewTruckVisible(false)}
        title={account.id ? "Edit Account" : "Add New Account"}
        onSubmit={onSubmit}
        onDataChanged={onDataChanged}
        data={account}
        isModalVisible={isNewTruckVisible}
        saving={saving}
      />
      {alert}
    </GridContainer>
  );
};

export default AccountsManagement;
